import React, { HTMLAttributes, ReactElement, Ref } from 'react';

import { GetLabelPropsOptions } from 'downshift';
import PropTypes from 'prop-types';

import { useDeprecate } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, {
  getInteractiveStyles,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import ToggleButton from './ToggleButton';
import { stripUnknownProps } from '../../helpers/internalHelpers';
import { GetToggleButtonProps } from '../../types';

type ControlLabelProps = StylableComponent<HTMLDivElement> &
  GetToggleButtonProps & {
    /**
     * @deprecated
     * getLabelProps should be applied to a <label> element that describes what the dropdown is for
     */
    getLabelProps?: (
      options?: GetLabelPropsOptions,
    ) => HTMLAttributes<HTMLLabelElement>;
    disabled?: boolean;
    isLoading?: boolean;
    error?: boolean;
    isOpen: boolean;
    isAutocomplete?: boolean;
    highDensity?: boolean;
    highlightButtonOnFocus?: boolean;
  };

const ControlLabelWrapped = (
  {
    className,
    style,
    disabled = false,
    isLoading = false,
    error,
    isOpen = false,
    getLabelProps,
    getToggleButtonProps,
    isAutocomplete,
    children,
    highDensity,
    highlightButtonOnFocus,
    onClick,
    ...props
  }: ControlLabelProps,
  ref: Ref<HTMLDivElement | HTMLLabelElement>,
) => {
  useDeprecate(
    !!getLabelProps,
    'Dropdown Control',
    'getLabelProps',
    'an external <label> element and apply `getLabelProps` to it',
  );

  // if this is not autocomplete, the ref gets provided to getToggleButtonProps to merge them
  const { ref: divRef, ...divProps } = isAutocomplete
    ? { ref }
    : getToggleButtonProps({
        // TODO: remove this cast once this is merged https://github.com/downshift-js/downshift/pull/1486
        ref: ref as Ref<HTMLButtonElement>,
        disabled,
      });

  const controlLabelProps = stripUnknownProps(props, [
    'openMenu',
    'selectItem',
    'selectedItem',
    'itemToString',
  ]);

  return (
    <div
      ref={divRef as Ref<HTMLDivElement>}
      data-testid={'Dropdown__control'}
      className={className}
      css={[
        {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `1px ${theme.spacing.S}px`,
          border: `1px solid ${theme.tokens.colors.border.value}`,
          borderRadius: theme.borderRadius.small,
          cursor: 'pointer',
          ...getInteractiveStyles(disabled),

          ...(isOpen && {
            borderColor: theme.tokens.colors.border.focused.value,
            boxShadow: theme.halos.focus,

            '&:hover': {
              boxShadow: theme.halos.focus,
            },
          }),

          ...(disabled && {
            backgroundColor:
              theme.tokens.colors.background.neutral.inactive.value,
            borderColor: theme.tokens.colors.border.inactive.value,
            color: theme.tokens.colors.text.inactive.value,
            cursor: 'not-allowed',
          }),

          ...(error &&
            !disabled && {
              borderColor: theme.tokens.colors.border.error.value,
            }),
        },
        style,
      ]}
      onClick={disabled ? undefined : onClick}
      {...divProps}
      {...controlLabelProps}
    >
      {children}
      {isLoading && (
        <ProgressRing
          size={ProgressRing.SIZES.XSMALL}
          style={{
            marginLeft: highDensity ? 0 : theme.spacing.XS,
          }}
        />
      )}
      <ToggleButton
        isOpen={isOpen}
        disabled={disabled}
        getToggleButtonProps={isAutocomplete ? getToggleButtonProps : undefined}
        style={{
          marginLeft: highDensity ? 0 : theme.spacing.XS,
          marginRight: theme.spacing.XXS,
          ...(disabled && { color: theme.tokens.colors.icon.inactive.value }),
        }}
        highlightButtonOnFocus={highlightButtonOnFocus}
      />
    </div>
  );
};

const ControlLabel = React.forwardRef(ControlLabelWrapped);

ControlLabel.displayName = 'ControlLabel';

ControlLabel.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** Function to get props for the label surrounding the control */
  getLabelProps: PropTypes.func,
  /** The content of te ControlContainer */
  children: PropTypes.any,
};

export default Object.assign(
  ControlLabel as (
    p: ControlLabelProps & { ref?: Ref<HTMLDivElement | HTMLLabelElement> },
  ) => ReactElement,
  { propTypes: ControlLabel.propTypes },
);
